import React, { useState, useEffect } from 'react';
import {
  DatagridConfigurable,
  FunctionField,
  List,
  Pagination,
  TextField as RaTextField,
  useDataProvider,
  useGetIdentity,
  usePermissions,
  useRefresh,
  useListContext,
} from 'react-admin';
import { Alert, AlertTitle, Box, Button } from '@mui/material';
import CustomBillingClientsFilter from './CustomBillingClientsFilter';
import EditIcon from '@mui/icons-material/Edit';
import { DEFAULTS } from '../constants/stringConstants';
import { toast } from 'react-toastify';
import EditBillingClientsDialog from './EditBillingClientsDialog';
import { BillingClientForm } from '../utils/DataTypes';
import { BILLING_CLIENT_FIELDS } from '../constants/stringConstants';
import { BILLING_CLIENTS_STRINGS } from '../constants/stringConstants';

const formatTimestamp = (timestamp?: string | number) => {
  if (!timestamp || timestamp === 'N/A') return '';
  const date = new Date(+timestamp);
  return date.toISOString().split('T')[0];
};

const BillingClientsContent = () => {
  const { setFilters } = useListContext();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const { identity } = useGetIdentity();
  const { permissions } = usePermissions();
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const initialState: BillingClientForm = BILLING_CLIENT_FIELDS.reduce((acc, key) => {
    acc[key] = '';
    return acc;
  }, {} as BillingClientForm);
  const [formValues, setFormValues] = useState<BillingClientForm>(initialState);
  const [originalValues, setOriginalValues] = useState<BillingClientForm>(initialState);
  const [missingInfoClients, setMissingInfoClients] = useState<any[]>([]);

  const fetchMissingClients = async () => {
    try {
      const [gpResponse, billingResponse] = await Promise.all([
        dataProvider.getList('BillingClients', {
          filter: { missing_gp_id: true },
          pagination: { page: 1, perPage: 100 },
        }),
        dataProvider.getList('BillingClients', {
          filter: { missing_billing_cycle: true },
          pagination: { page: 1, perPage: 100 },
        }),
      ]);

      const merged = [...gpResponse.data, ...billingResponse.data].filter(
        (value, index, self) => index === self.findIndex((v) => v.id === value.id)
      );

      setMissingInfoClients(merged);
    } catch (error) {
      toast.error(BILLING_CLIENTS_STRINGS.TOAST.FETCH_ERROR);
    }
  };
  useEffect(() => {
    fetchMissingClients();
  }, [dataProvider]);

  const handleEdit = (record: any) => {
    setEditingRowId(record.id);
    const initialValues: BillingClientForm = BILLING_CLIENT_FIELDS.reduce((acc, key) => {
      acc[key] = record[key] || '';
      return acc;
    }, {} as BillingClientForm);

    setFormValues(initialValues);
    setOriginalValues(initialValues);
    setIsDialogOpen(true);
  };

  const getChangedFields = () => {
    const changedFields: Record<string, any> = {};

    Object.keys(formValues).forEach((key) => {
      if (formValues[key as keyof typeof formValues] !== originalValues[key as keyof typeof originalValues]) {
        const currentValue = formValues[key as keyof typeof formValues];
        changedFields[key] = currentValue === null ? '' : currentValue;
      }
    });

    return changedFields;
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingRowId(null);
  };

  const handleSave = async () => {
    if (formValues.invoice_breakout === '') {
      formValues.invoice_breakout_description = '';
    }
    const changedFields = getChangedFields();

    if (Object.keys(changedFields).length === 0) {
      toast.info(BILLING_CLIENTS_STRINGS.TOAST.NO_CHANGES);
      handleDialogClose();
      return;
    }

    const payload = {
      billing_client_key_id: editingRowId,
      last_updated_by: identity?.fullName || DEFAULTS.UNKNOWN_USER,
      ...changedFields,
    };

    try {
      const response = await dataProvider.update('BillingClients', {
        id: editingRowId,
        data: payload,
        previousData: originalValues,
      });
      toast.success(BILLING_CLIENTS_STRINGS.TOAST.SUCCESS);
      refresh();
      fetchMissingClients();
    } catch (error) {
      toast.error(BILLING_CLIENTS_STRINGS.TOAST.ERROR);
    }
    handleDialogClose();
  };

  return (
    <Box>
      {missingInfoClients.length > 0 && (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={() => setFilters({ missing_info: true })}>
              {BILLING_CLIENTS_STRINGS.ALERT.VIEW_CLIENTS_BUTTON}
            </Button>
          }
          sx={{ mb: 2, borderRadius: 2, boxShadow: 1 }}
        >
          <AlertTitle>{BILLING_CLIENTS_STRINGS.ALERT.TITLE}</AlertTitle>
          {BILLING_CLIENTS_STRINGS.ALERT.DESCRIPTION(missingInfoClients.length)}
        </Alert>
      )}

      <DatagridConfigurable
        bulkActionButtons={false}
        sx={{
          tableLayout: 'fixed',
          '& .MuiTableCell-head': {
            textAlign: 'center',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          },
          '& .MuiTableCell-body': {
            textAlign: 'center',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
          },
          '& .createdByCol, & .createdDateCol': {
            width: '100px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
          '& .billingCycleCol': {
            width: '120px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
          '& .actionsCol': {
            width: '70px',
            textAlign: 'center',
          },
          '& .updatedByCol, & .lastUpdatedDateCol': {
            width: '100px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
          '& .wrapped-header': {
            width: '120px',
            whiteSpace: 'normal',
            textAlign: 'center',
          },
        }}
      >
        <RaTextField
          source="billing_client_number"
          label={BILLING_CLIENTS_STRINGS.LABELS.BILLING_CLIENT_NUMBER}
          sortable={false}
          sx={{ minWidth: '150px' }}
        />
        <RaTextField
          source="billing_client_name"
          label={BILLING_CLIENTS_STRINGS.LABELS.BILLING_CLIENT_NAME}
          sx={{ minWidth: '180px' }}
          sortable={false}
        />
        <RaTextField source="billing_tpa_name" label={BILLING_CLIENTS_STRINGS.LABELS.TPA_NAME} sortable={false} />
        <RaTextField source="accounting_client_id" label={BILLING_CLIENTS_STRINGS.LABELS.GP_ID} sortable={false} />
        <RaTextField
          source="billing_cycle"
          label={BILLING_CLIENTS_STRINGS.LABELS.BILLING_CYCLE}
          sortable={false}
          headerClassName="billingCycleCol"
          cellClassName="billingCycleCol"
        />
        <RaTextField
          source="invoice_detail_location_yn"
          label={BILLING_CLIENTS_STRINGS.LABELS.INVOICE_DETAIL_LOCATION}
          headerClassName="wrapped-header"
          sortable={false}
          sx={{ minWidth: '120px' }}
        />
        <RaTextField
          source="invoice_detail_member_name_yn"
          label={BILLING_CLIENTS_STRINGS.LABELS.INVOICE_DETAIL_MEMBER_NAME}
          headerClassName="wrapped-header"
          sortable={false}
          sx={{ minWidth: '120px' }}
        />
        <RaTextField
          source="invoice_breakout"
          label={BILLING_CLIENTS_STRINGS.LABELS.INVOICE_BREAKOUT}
          headerClassName="wrapped-header"
          sortable={false}
          sx={{ minWidth: '120px' }}
        />
        <RaTextField
          source="invoice_breakout_description"
          label={BILLING_CLIENTS_STRINGS.LABELS.INVOICE_BREAKOUT_DESC}
          headerClassName="wrapped-header"
          sortable={false}
          sx={{ minWidth: '150px' }}
        />
        <FunctionField
          label={BILLING_CLIENTS_STRINGS.LABELS.CREATED_DATE}
          render={(record: any) => formatTimestamp(record.created_ts)}
          sortable={false}
          headerClassName="createdDateCol"
          cellClassName="createdByCol"
        />
        <RaTextField
          source="last_updated_by"
          label={BILLING_CLIENTS_STRINGS.LABELS.UPDATED_BY}
          sortable={false}
          headerClassName="updatedByCol"
          cellClassName="updatedByCol"
        />
        <FunctionField
          label={BILLING_CLIENTS_STRINGS.LABELS.LAST_UPDATED_DATE}
          render={(record: any) => formatTimestamp(record.last_updated_ts)}
          sortable={false}
          headerClassName="lastUpdatedDateCol"
          cellClassName="lastUpdatedDateCol"
        />
        {permissions.adminPermission && (
          <FunctionField
            label={BILLING_CLIENTS_STRINGS.LABELS.ACTIONS}
            headerClassName="actionsCol"
            cellClassName="actionsCol"
            render={(record: any) => (
              <button
                onClick={() => handleEdit(record)}
                style={{ border: 'none', background: 'none', cursor: 'pointer' }}
              >
                <EditIcon style={{ color: '#00909A' }} />
              </button>
            )}
          />
        )}
      </DatagridConfigurable>

      {isDialogOpen && (
        <EditBillingClientsDialog
          isDialogOpen={isDialogOpen}
          formValues={formValues}
          setFormValues={setFormValues}
          handleDialogClose={handleDialogClose}
          handleSave={handleSave}
        />
      )}
    </Box>
  );
};

const BillingClients: React.FC = () => {
  return (
    <List
      perPage={10}
      pagination={<Pagination />}
      resource="BillingClients"
      title="Billing Clients"
      filters={<CustomBillingClientsFilter />}
      actions={false}
    >
      <BillingClientsContent />
    </List>
  );
};

export default BillingClients;
